import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import { formatNumberCH } from "../../../../lib/utils/format";

export interface D3AreaChartProps {
  data?: Array<any>;
  dataKey?: string;
  width?: number;
  height?: number;
  margin?: { top: number; right: number; left: number; bottom: number };
  strokeColor?: string;
  strokeWidth?: number;
  fillColor?: string;
  fillOpacity?: number;
  gridColor?: string;
  axisColor?: string;
  yAxisLabel?: string;
  yTickCount?: number | null;
  yTickValues?: number[] | null;
  showVolume?: boolean;
  onTooltipShow?: (d: any, evt: any) => void;
  onTooltipHide?: () => void;
  children?: React.ReactNode;
}

/**
 * Area chart component using D3 for timeline visualizations
 *
 * Features:
 * - Gradient fill with clipping for clean edges
 * - Intelligent tick selection (heuristics for small integer ranges)
 * - Proximity-based hover detection for better mobile/touch UX
 * - Responsive tooltip positioning within chart bounds
 */
const D3AreaChart: React.FC<D3AreaChartProps> = ({
  data = [],
  dataKey = "value",
  width = 400,
  height = 440,
  margin = { top: 50, right: 50, left: 60, bottom: 60 },
  strokeColor = "#E84A5F",
  strokeWidth = 2,
  fillColor = "#E84A5F",
  fillOpacity = 0.8,
  gridColor = "#E2E8F0",
  axisColor = "#4A5568",
  yAxisLabel = "",
  yTickCount = null,
  yTickValues = null,
  showVolume = false,
  onTooltipShow,
  onTooltipHide,
  children,
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!data?.length || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const svgRect = svgRef.current.getBoundingClientRect();
    const renderedWidth = svgRect && svgRect.width ? svgRect.width : width;
    const chartWidth = renderedWidth - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Create clipping path to prevent area from overflowing chart bounds
    const defs = svg.append("defs");
    const clipId = `clip-${Math.random().toString(36).slice(2, 9)}`;
    defs
      .append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", chartWidth)
      .attr("height", chartHeight);

    // Define gradient for area fill (fade from solid color to transparent)
    const gradient = defs
      .append("linearGradient")
      .attr("id", `area-gradient-${dataKey}`)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradient
      .append("stop")
      .attr("offset", "5%")
      .attr("stop-color", fillColor)
      .attr("stop-opacity", fillOpacity);
    gradient
      .append("stop")
      .attr("offset", "95%")
      .attr("stop-color", fillColor)
      .attr("stop-opacity", 0.1);

    svg
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "none")
      .style("pointer-events", "none");

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const xScale: any = d3
      .scaleLinear()
      .domain(d3.extent(data, (d: any) => d.year) as [number, number])
      .range([0, chartWidth]);

    const rawMax = (d3.max(data, (d: any) => d[dataKey]) as number | undefined) || 0;

    // Calculate Y-axis scale with intelligent tick selection
    // For small ranges (e.g., 1-20 deals), we prefer integer ticks to avoid 2.5, 3.75 etc.
    const defaultYTickCount = Math.max(3, Math.min(6, Math.floor(chartHeight / 70)));
    const effectiveYTickCount = yTickCount != null ? yTickCount : defaultYTickCount;

    const step = Math.max(1, d3.tickStep(0, rawMax, effectiveYTickCount));
    const yTop = Math.max(step, Math.ceil(rawMax / step) * step);

    const yScale: any = d3.scaleLinear().domain([0, yTop]).range([chartHeight, 0]);

    // X-axis tick selection: show all years if consecutive, otherwise sample
    const years = Array.from(new Set(data.map((d: any) => d.year))).sort(
      (a: number, b: number) => a - b
    );
    const maxTicks = Math.max(1, Math.floor(chartWidth / 60));
    const isConsecutive =
      years.length > 1 && years.every((y, i) => i === 0 || y === years[i - 1] + 1);
    let tickValues: number[] = [];
    if (isConsecutive || years.length <= maxTicks) {
      tickValues = years.slice();
    } else {
      // Sample years to avoid overcrowding
      const step = Math.max(1, Math.ceil(years.length / maxTicks));
      for (let i = 0; i < years.length; i += step) tickValues.push(years[i]);
      if (tickValues[tickValues.length - 1] !== years[years.length - 1]) {
        tickValues.push(years[years.length - 1]);
      }
    }

    // Y-axis ticks: for count data with small ranges, enforce integer ticks
    let yTicks: number[] = yTickValues
      ? (yTickValues as number[])
      : (yScale.ticks(effectiveYTickCount) as number[]);
    if (!showVolume && yTop <= 20) {
      yTicks = yTicks.filter((tick) => Number.isInteger(tick) && tick >= 0);
      if (yTicks.length < 2) {
        yTicks = [];
        const maxTick = Math.ceil(yTop);
        const step = Math.max(1, Math.ceil(maxTick / Math.min(5, maxTick)));
        for (let i = 0; i <= maxTick; i += step) yTicks.push(i);
      }
    }

    // Draw grid lines
    g.selectAll(".grid-x")
      .data(tickValues)
      .enter()
      .append("line")
      .attr("class", "grid-x")
      .attr("x1", (d) => xScale(d))
      .attr("x2", (d) => xScale(d))
      .attr("y1", 0)
      .attr("y2", chartHeight)
      .style("stroke", gridColor)
      .style("stroke-width", 1)
      .style("stroke-dasharray", "3,3")
      .style("opacity", 0.7);

    g.selectAll(".grid-y")
      .data(yTicks)
      .enter()
      .append("line")
      .attr("class", "grid-y")
      .attr("x1", 0)
      .attr("x2", chartWidth)
      .attr("y1", (d) => yScale(d))
      .attr("y2", (d) => yScale(d))
      .style("stroke", gridColor)
      .style("stroke-width", 1)
      .style("stroke-dasharray", "3,3")
      .style("opacity", 0.7);

    const area = d3
      .area<any>()
      .x((d) => xScale(d.year))
      .y0(chartHeight)
      .y1((d) => yScale(d[dataKey]))
      .curve(d3.curveMonotoneX);

    const line = d3
      .line<any>()
      .x((d) => xScale(d.year))
      .y((d) => yScale(d[dataKey]))
      .curve(d3.curveMonotoneX);

    const plot = g.append("g").attr("clip-path", `url(#${clipId})`);

    plot
      .append("path")
      .datum(data as any)
      .attr("fill", `url(#area-gradient-${dataKey})`)
      .attr("d", area as any);

    plot
      .append("path")
      .datum(data as any)
      .attr("fill", "none")
      .attr("stroke", strokeColor)
      .attr("stroke-width", strokeWidth)
      .attr("d", line as any);

    const xAxis = g
      .append("g")
      .attr("transform", `translate(0,${chartHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(tickValues)
          .tickFormat(d3.format("d") as any)
      );

    xAxis
      .selectAll("text")
      .style("font-size", "12px")
      .style("fill", axisColor)
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .attr("dx", "-0.8em")
      .attr("dy", "0.15em");

    (xAxis as any).selectAll("line, path").style("stroke", axisColor);
    (xAxis as any).select(".domain").remove();
    g.append("line")
      .attr("class", "axis-domain-x")
      .attr("x1", 0)
      .attr("x2", chartWidth)
      .attr("y1", chartHeight + 0.5)
      .attr("y2", chartHeight + 0.5)
      .style("stroke", axisColor)
      .style("stroke-width", 1)
      .style("shape-rendering", "crispEdges");

    (xAxis as any).selectAll(".tick line").style("stroke-linecap", "butt");

    const yAxis = g.append("g").call(
      d3
        .axisLeft(yScale)
        .tickValues(yTicks)
        .tickFormat((d: any) => formatNumberCH(d, showVolume ? "auto" : 0) as any)
    );

    (yAxis as any).selectAll("text").style("font-size", "12px").style("fill", axisColor);
    (yAxis as any).selectAll("line, path").style("stroke", axisColor);

    // Remove duplicate Y-axis labels (can occur with custom tick values)
    try {
      const seen = new Set<string>();
      (yAxis as any)
        .selectAll("text")
        .nodes()
        .forEach((n: any) => {
          const key = `${n.textContent}|${n.getAttribute("y")}`;
          if (seen.has(key)) n.remove();
          else seen.add(key);
        });
    } catch (e) {}

    (yAxis as any).select(".domain").remove();
    g.append("line")
      .attr("class", "axis-domain")
      .attr("x1", 0.5)
      .attr("x2", 0.5)
      .attr("y1", 0)
      .attr("y2", chartHeight)
      .style("stroke", axisColor)
      .style("stroke-width", 1)
      .style("shape-rendering", "crispEdges");

    (yAxis as any).selectAll(".tick line").style("stroke-linecap", "butt");

    if (yAxisLabel) {
      g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("y", 0 - margin.left)
        .attr("x", 0 - chartHeight / 2)
        .attr("dy", "1em")
        .style("text-anchor", "middle")
        .style("font-size", "12px")
        .style("fill", axisColor)
        .text(yAxisLabel);
    }

    const tooltip = d3.select(tooltipRef.current);

    // Proximity-based hover detection: find closest year within threshold
    // This provides better UX than requiring exact pixel hover, especially on touch devices
    const overlay = g
      .append("rect")
      .attr("width", chartWidth)
      .attr("height", chartHeight)
      .attr("fill", "transparent")
      .style("pointer-events", "all")
      .on("mousemove", function (event: any) {
        const [mouseX] = d3.pointer(event);

        const xPositions = data.map((d: any) => xScale(d.year));
        let minIndex = -1;
        let minDist = Infinity;
        xPositions.forEach((xp: number, i: number) => {
          const dist = Math.abs(mouseX - xp);
          if (dist < minDist) {
            minDist = dist;
            minIndex = i;
          }
        });

        const HIT_THRESHOLD_PX = 15; // Only show tooltip if within 15px of data point

        if (minIndex >= 0 && minDist <= HIT_THRESHOLD_PX) {
          const closestData: any = data[minIndex];
          const value = closestData[dataKey];
          const formattedValue = showVolume
            ? formatNumberCH(value, "auto")
            : formatNumberCH(value, 0);

          const xPos = margin.left + xScale(closestData.year);
          const yPos = margin.top + yScale(closestData[dataKey]);

          tooltip
            .style("opacity", 1)
            .style("top", `${yPos}px`)
            .style("transform", "translate(0, -120%)")
            .style("background", "#ffffff")
            .style("border", "1px solid #E2E8F0")
            .style("padding", "8px")
            .style("border-radius", "8px")
            .style("color", "#1F2937")
            .style("box-shadow", "0 6px 20px rgba(16,24,40,0.08)")
            .style("white-space", "nowrap")
            .html(`${closestData.year}: ${formattedValue}`);

          // Clamp tooltip horizontal position to keep it within chart bounds
          const node = tooltip.node() as HTMLDivElement | null;
          const ttWidth = node ? (node.getBoundingClientRect().width as number) : 0;
          let left = xPos - ttWidth / 2;
          const minLeft = margin.left + 6;
          const maxLeft = margin.left + chartWidth - ttWidth - 6;
          left = Math.max(minLeft, Math.min(left, maxLeft));
          tooltip.style("left", `${left}px`);

          if (onTooltipShow) onTooltipShow(closestData, event);
          (overlay as any).style("cursor", "pointer");
        } else {
          tooltip.style("opacity", 0);
          if (onTooltipHide) onTooltipHide();
          (overlay as any).style("cursor", "default");
        }
      })
      .on("mouseleave", function () {
        tooltip.style("opacity", 0);
        if (onTooltipHide) onTooltipHide();
      });
  }, [
    data,
    dataKey,
    width,
    height,
    margin,
    strokeColor,
    strokeWidth,
    fillColor,
    fillOpacity,
    gridColor,
    axisColor,
    yAxisLabel,
    yTickCount,
    yTickValues,
    showVolume,
    onTooltipShow,
    onTooltipHide,
  ]);

  // Debug info for development (shown when window.__D3_DEBUG__ = true)
  const [debugInfo, setDebugInfo] = useState<{
    renderedWidth: number | null;
    chartWidth: number | null;
  }>({ renderedWidth: null, chartWidth: null });

  useEffect(() => {
    if (!svgRef.current) return;
    const svgRect = svgRef.current.getBoundingClientRect();
    const renderedWidth = svgRect && svgRect.width ? svgRect.width : width;
    const chartWidth = renderedWidth - margin.left - margin.right;
    setDebugInfo({ renderedWidth: Math.round(renderedWidth), chartWidth: Math.round(chartWidth) });
  }, [width, margin.left, margin.right]);

  return (
    <div style={{ position: "relative" }}>
      {children}
      <svg
        ref={svgRef}
        style={{ position: "relative", zIndex: 40 }}
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
      />
      <div
        ref={tooltipRef}
        style={{
          position: "absolute",
          pointerEvents: "none",
          opacity: 0,
          transition: "opacity 0.2s",
          zIndex: 50,
        }}
      />
      {(window as any) && (window as any).__D3_DEBUG__ ? (
        <div
          style={{
            position: "absolute",
            left: 6,
            bottom: 6,
            background: "rgba(0,0,0,0.6)",
            color: "#fff",
            padding: "6px 8px",
            borderRadius: 6,
            fontSize: 12,
            zIndex: 60,
          }}
        >
          <div>renderedWidth: {debugInfo.renderedWidth ?? "-"}px</div>
          <div>chartWidth: {debugInfo.chartWidth ?? "-"}px</div>
        </div>
      ) : null}
    </div>
  );
};

export default D3AreaChart;
